import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  PlayCircle,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  ExternalLink,
  Trash2,
  Download,
  Sparkles,
  Film
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Dailymotion» (solo admin): cuenta, vídeos horizontales y subidas. Habla solo con /api/admin/dm/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/dm`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  writing: { label: "Escribiendo guion", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  uploading: { label: "Subiendo", style: "bg-sky-500/15 text-sky-300 border-sky-500/30", busy: true },
  ready: { label: "Listo, sin subir", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "Publicado", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};

const CATEGORIES = {
  fun: "Humor y curiosidades", tech: "Tecnología", lifestyle: "Estilo de vida", school: "Educación",
  news: "Noticias", people: "Sociedad", animals: "Animales", travel: "Viajes", auto: "Motor", sport: "Deporte"
};

export const DailymotionView = () => {
  const [account, setAccount] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [keyForm, setKeyForm] = useState({ api_key: "", api_secret: "" });
  const [create, setCreate] = useState({ topic: "", category: "fun", upload: true });
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const timer = useRef(null);

  const loadAccount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account`, opts());
      setAccount(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar la cuenta de Dailymotion." });
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  useEffect(() => {
    loadAccount();
    loadVideos();
  }, [loadAccount, loadVideos]);

  // Mientras se crea un vídeo, refrescar la lista cada 5 s
  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy);
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadAccount(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadAccount]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      await fn();
      if (okText) setFeedback({ type: "success", text: okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking(null);
    }
  };

  const saveKey = () => run("key", async () => {
    const res = await axios.post(`${API}/account`, keyForm, opts());
    setAccount(res.data);
    setKeyForm({ api_key: "", api_secret: "" });
  }, "Cuenta de Dailymotion conectada.");

  const refreshAccount = () => run("refresh", async () => {
    const res = await axios.post(`${API}/account/refresh`, {}, opts());
    setAccount(res.data);
  }, "Conexión comprobada.");

  const chooseProfile = (profile_id) => run("profile", async () => {
    const res = await axios.post(`${API}/account/profile`, { profile_id }, opts());
    setAccount(res.data);
  }, "Canal elegido.");

  const disconnect = () => {
    if (!window.confirm("¿Desconectar la cuenta de Dailymotion? Se borrará la clave guardada.")) return;
    run("disconnect", async () => {
      const res = await axios.post(`${API}/account/disconnect`, {}, opts());
      setAccount(res.data);
    }, "Cuenta desconectada.");
  };

  const createVideo = () => run("create", async () => {
    await axios.post(`${API}/videos/create`, create, opts());
    setCreate((c) => ({ ...c, topic: "" }));
    setBusy(true);
    loadVideos();
  }, "Creando el vídeo. Aparecerá abajo en unos minutos.");

  const uploadVideo = (v) => run(`up-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/upload`, {}, opts());
    loadVideos();
    loadAccount();
  }, "Vídeo subido a Dailymotion.");

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en Dailymotion, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando Dailymotion...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  return (
    <div data-testid="dm-view" className="space-y-6">
      {/* Cuenta */}
      <div className="bg-gradient-to-br from-[#12141D] to-[#111A26] border border-sky-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-sky-500/15 border border-sky-500/40 text-sky-400 flex items-center justify-center shrink-0">
              <PlayCircle className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Dailymotion</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">Solo admin</span>
                {account.connected ? (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                    Conectado: {account.profile_name}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Vídeos horizontales (1920×1080) que se suben a tu canal de Dailymotion. Totalmente aparte de YouTube:
                no aparecen en «Mis Shorts» ni gastan créditos ni cuota de YouTube.
              </p>
            </div>
          </div>
          {account.connected && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-slate-400 font-mono">Subidas hoy: {account.uploads_today}/{account.max_uploads}</span>
              <Button variant="ghost" size="sm" onClick={refreshAccount} disabled={working !== null}
                className="h-8 text-xs text-slate-300 hover:text-white flex items-center gap-1">
                <RefreshCw className={`w-3.5 h-3.5 ${working === "refresh" ? "animate-spin" : ""}`} /> Comprobar
              </Button>
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${
            feedback.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border border-red-500/30 text-red-300"
          }`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}

        {account.connected && account.profiles.length > 1 && (
          <label className="flex items-center gap-2 text-xs text-slate-300">
            Subir al canal:
            <select value={account.profile_id || ""} onChange={(e) => chooseProfile(e.target.value)} disabled={working !== null}
              className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white">
              {account.profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}

        {!account.connected && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-1.5">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-sky-400" /> Cómo conseguir la clave</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>Entra en <a href="https://www.dailymotion.com/partner/api-keys/" target="_blank" rel="noopener noreferrer" className="text-sky-300 underline">Dailymotion Studio → Organización → Claves de API</a> con tu cuenta (propietario o admin).</li>
                <li>Pulsa «Crear clave de API» y elige <strong className="text-slate-200">clave privada</strong>.</li>
                <li>Dale acceso a tu canal y activa los permisos de <strong className="text-slate-200">gestionar vídeos</strong> y <strong className="text-slate-200">leer la cuenta</strong>.</li>
                <li>Copia la clave y el secreto (el secreto solo se ve una vez) y pégalos aquí.</li>
              </ol>
              {account.last_error && <p className="text-red-400 break-words pt-1">Último error: {account.last_error}</p>}
            </div>
            <div className="space-y-2">
              <input type="text" placeholder="Clave de API" value={keyForm.api_key} autoComplete="off"
                onChange={(e) => setKeyForm((f) => ({ ...f, api_key: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/60" />
              <input type="password" placeholder="Secreto de API" value={keyForm.api_secret} autoComplete="new-password"
                onChange={(e) => setKeyForm((f) => ({ ...f, api_secret: e.target.value }))}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/60" />
              <Button data-testid="dm-connect-btn" onClick={saveKey} disabled={working !== null || !keyForm.api_key || !keyForm.api_secret}
                className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-black text-xs flex items-center justify-center gap-1.5">
                {working === "key" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                Conectar Dailymotion
              </Button>
              <p className="text-[10px] text-slate-500">La clave se guarda solo en el servidor y nunca se devuelve al navegador.</p>
            </div>
          </div>
        )}
      </div>

      {/* Crear un vídeo a mano */}
      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3">
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-sky-400" /> Crear un vídeo sobre un tema</h4>
        <div className="flex flex-col lg:flex-row gap-2">
          <input type="text" maxLength={200} placeholder="Por ejemplo: por qué el café a veces da sueño"
            value={create.topic} onChange={(e) => setCreate((c) => ({ ...c, topic: e.target.value }))}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/60" />
          <select value={create.category} onChange={(e) => setCreate((c) => ({ ...c, category: e.target.value }))}
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
            {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-300 px-1">
            <input type="checkbox" checked={create.upload} onChange={(e) => setCreate((c) => ({ ...c, upload: e.target.checked }))} className="accent-sky-500 w-4 h-4" />
            Subir al terminar
          </label>
          <Button data-testid="dm-create-btn" onClick={createVideo} disabled={working !== null || busy || create.topic.trim().length < 5}
            title={busy ? "Ya se está creando un vídeo; espera a que termine" : ""}
            className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-black text-xs flex items-center gap-1.5">
            {working === "create" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
            Crear vídeo
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">
          Se hace un vídeo a la vez. Si en ese momento se está montando un Short de YouTube, el de Dailymotion espera su turno.
        </p>
      </div>

      {/* Vídeos */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-black text-white">Vídeos de Dailymotion ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={loadVideos} className="h-7 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${anyBusy ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
        {videos.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            Todavía no hay vídeos. Crea uno arriba o desde el Radar Dailymotion.
          </div>
        ) : (
          <div className="space-y-2">
            {videos.map((v) => {
              const st = STATUS[v.status] || STATUS.failed;
              const fileUrl = `${API}/videos/${v.id}/file`;
              const hasFile = v.video_url && !v.video_deleted;
              return (
                <div key={v.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 space-y-2 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${st.style}`}>
                          {st.busy && <RefreshCw className="w-2.5 h-2.5 inline mr-1 animate-spin" />}{st.label}
                        </span>
                        <span className="text-[10px] uppercase font-bold text-sky-300">{CATEGORIES[v.category] || v.category}</span>
                        <span className="text-[10px] text-slate-500">{v.source === "radar" ? "Del Radar" : "Manual"} · {new Date(v.created_at).toLocaleString("es-ES")}</span>
                        {v.duration && <span className="text-[10px] text-slate-500">{Math.round(v.duration)} s</span>}
                      </div>
                      <p className="text-sm font-bold text-white break-words">{v.title}</p>
                      {v.progress && <p className={`text-[11px] break-words ${v.status === "failed" ? "text-red-400" : "text-slate-400"}`}>{v.progress}</p>}
                      {v.trend_title && <p className="text-[11px] text-slate-500 truncate">Tendencia de origen: {v.trend_title}</p>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {hasFile && (
                        <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)}
                          className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <PlayCircle className="w-3 h-3" /> {preview === v.id ? "Ocultar" : "Ver"}
                        </Button>
                      )}
                      {hasFile && (
                        <a href={fileUrl} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 rounded-md hover:bg-white/5">
                          <Download className="w-3 h-3" /> Descargar
                        </a>
                      )}
                      {v.status === "ready" && hasFile && (
                        <Button size="sm" onClick={() => uploadVideo(v)} disabled={working !== null || !account.connected}
                          title={account.connected ? "" : "Conecta tu cuenta de Dailymotion"}
                          className="h-7 px-2.5 text-[11px] font-bold bg-sky-500 hover:bg-sky-400 text-slate-950 flex items-center gap-1">
                          {working === `up-${v.id}` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          Subir a Dailymotion
                        </Button>
                      )}
                      {v.dm_url && (
                        <a href={v.dm_url} target="_blank" rel="noopener noreferrer"
                          className="h-7 px-2.5 text-[11px] font-bold text-emerald-300 hover:text-emerald-200 flex items-center gap-1 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                          <ExternalLink className="w-3 h-3" /> Ver en Dailymotion
                        </a>
                      )}
                      {!st.busy && (
                        <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null}
                          className="h-7 px-2 text-[11px] text-slate-500 hover:text-red-300 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {preview === v.id && hasFile && (
                    <video src={fileUrl} controls className="w-full max-w-3xl aspect-video rounded-lg bg-black" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
